import Isotope from 'isotope-layout';
import imagesLoaded from 'imagesloaded';
import { debugLog } from './enci.log.js';

// Store Isotope instances
const isotopeInstances = new Map();

/**
 * Initializes and configures the Isotope layout library for dynamic grid layouts.
 * Targets elements within a container class '.grid' for Isotope's manipulation, specifying items by '.grid-item' and setting the layout mode to 'fitRows'.
 * Defines custom filter functions to allow for complex filtering criteria, such as filtering by numerical value or string patterns.
 * Sets up event listeners on '.portfolio-filter' buttons to apply these filters dynamically to the grid items based on data attributes.
 * Also manages active state toggling for filter buttons to visually indicate the currently applied filter.
 *
 * @function createIsotopeGridItems
 */
export async function initializeIsotopeGrid(selector) {
  debugLog('initializeIsotopeGrid');

  const x = 1;
  if (x==1) return;

  // Initialize Isotope
  const grid = document.querySelector(selector);
  if (!grid) {
    debugLog('initializeIsotopeGrid >> No grid exists.', {
      type: 'error',
    });
    return;
  }

  // Create new Isotope instance and store it
  const iso = new Isotope(grid, {
    itemSelector: '.grid-item',
    layoutMode: 'fitRows',
  });

  isotopeInstances.set(selector, iso);

  // Use imagesLoaded to handle image loading
  imagesLoaded(grid).on('progress', () => {
    // layout Isotope after each image loads
    iso.layout();
  });

  const filters = document.querySelectorAll('.portfolio-filter button');
  if (!filters.length) {
    debugLog('No filters exist.', { type: 'error' });
    return;
  }

  // Bind filter button click events
  filters.forEach(button => {
    button.addEventListener('click', () => {
      const filterValue = button.getAttribute('data-filter');
      arrangeGrid(selector, filterValue);
    });
  });

  // Handle is-checked class for buttons
  document.querySelectorAll('.portfolio-filter').forEach(buttonGroup => {
    buttonGroup.addEventListener('click', event => {
      // Ensure the click is on a button
      if (event.target.tagName === 'BUTTON') {
        // Remove 'is-checked' from any button in this group
        buttonGroup.querySelectorAll('button.is-checked').forEach(activeButton => {
          activeButton.classList.remove('is-checked');
        });
        // Add 'is-checked' to the clicked button
        event.target.classList.add('is-checked');
      }
    });
  });
}

// Add this function to your enci.isotope.js file
export function arrangeGrid(selector, filterValue) {
  const iso = isotopeInstances.get(selector);
  if (!iso) {
    debugLog('arrangeGrid >> No Isotope instance exists for selector: ' + selector, {
      type: 'error',
    });
    return;
  }

  iso.arrange({ filter: filterValue });

  // Update is-checked class
  const buttons = document.querySelectorAll('.portfolio-filter button');
  buttons.forEach(button => {
    if (button.getAttribute('data-filter') === filterValue) {
      button.classList.add('is-checked');
    } else {
      button.classList.remove('is-checked');
    }
  });
}

/**
 * Initializes a Masonry layout using the Isotope library on a specified container.
 * Targets the container with the class '.masonry' for applying the Masonry layout, using '.masonry-brick' as the item selector.
 * Dynamically applies filtering based on buttons within '.portfolio-filter', allowing items to be filtered according to data attributes.
 * Handles the absence of the Masonry container or filters by logging an error, preventing script execution errors if elements are missing.
 *
 * @function createIsotopeMasonryItems
 */
export async function initializeMasonry() {
  debugLog('initializeMasonry');

  const masonry = document.querySelector('.masonry');

  if (!masonry) {
    debugLog('initializeMasonry >> No masonry exists.', {
      type: 'error',
    });
    return;
  }

  const iso = new Isotope(masonry, {
    itemSelector: '.masonry-brick',
    resizable: true,
  });

  isotopeInstances.set('.masonry', iso);

  const filters = document.querySelectorAll('.portfolio-filter button');
  if (!filters.length) {
    debugLog('No filters exist.', { type: 'error' });
    return;
  }

  filters.forEach(filter => {
    filter.addEventListener('click', function () {
      const filterValue = this.getAttribute('data-filter');
      arrangeGrid('.masonry', filterValue);
    });
  });

  // Handle is-checked class for buttons
  filters.forEach(buttonGroup => {
    buttonGroup.addEventListener('click', event => {
      if (event.target.tagName === 'BUTTON') {
        const activeButton = buttonGroup.querySelector('.is-checked');
        if (activeButton) {
          activeButton.classList.remove('is-checked');
        }
        event.target.classList.add('is-checked');
      }
    });
  });
}

// Add a cleanup function for when components are disposed
export function cleanup(selector) {
  const iso = isotopeInstances.get(selector);
  if (iso) {
    iso.destroy();
    isotopeInstances.delete(selector);
  }
}